import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { DashboardComponentType } from '@/features/dashboard'

export interface Feature<Props = any> {
  name: string
  enabled: boolean
  type?: DashboardComponentType
  access?: string[]
  title?: LocaleString
  info?: LocaleString
  component?: string
  titleShort?: LocaleString
  roleAccess?: string[]
  realmAccess?: string[]
  // Org-flow only: the organization feature `key`(s) that unlock this node, matched against the
  // org's purchased features from the caller's own GET /api/me/access overview (its entitlement)
  // — see filterByOrgFeatures. Absent means "always shown", same permissive-by-default
  // convention as roleAccess, so nodes need no annotation until the real feature-tak
  // catalog is confirmed against the backend.
  orgFeatureKeys?: string[]
  isRoute?: boolean
  props?: Props
  parent?: Feature<any>
  children?: Feature<any>[]
}

export function filterAccess(feature: Feature, realmId: string, featureAccess: string[]): Feature {
  return {
    ...feature,
    children: feature.children
      ?.map((child) => filterAccess(child, realmId, featureAccess))
      .filter((child) => {
        if (!child.enabled) return false
        if (child.realmAccess && realmId && !child.realmAccess.includes(realmId)) return false
        return child.roleAccess?.some((access) => featureAccess.includes(access)) ?? true
      })
  }
}

// Strips `enabled: false` nodes from a feature tree. Unlike filterAccess, this
// only considers the active/inactive flag — no roleAccess/realmAccess gating.
// Like filterAccess, it only checks `enabled` on children, never on the root
// itself, so a root with `enabled: false` passes through unfiltered.
export function filterInactive(feature: Feature): Feature {
  return {
    ...feature,
    children: feature.children?.map(filterInactive).filter((child) => child.enabled)
  }
}

// Org-flow equivalent of filterAccess: prunes the static feature tree down to what the
// organization has actually purchased, using access-service's flat feature-tak key list
// (org-flow has no realm-derived JWT roles to gate on, so this replaces roleAccess/realmAccess
// checks rather than adding to them). Like filterAccess, only checks `enabled` on children.
export function filterByOrgFeatures(feature: Feature, orgFeatureKeys: string[]): Feature {
  return {
    ...feature,
    children: feature.children
      ?.map((child) => filterByOrgFeatures(child, orgFeatureKeys))
      .filter((child) => {
        if (!child.enabled) return false
        return child.orgFeatureKeys?.some((key) => orgFeatureKeys.includes(key)) ?? true
      })
  }
}

// Org-flow's per-member gate: keycloak-service applies the `access` FEAT_* lists for realm users
// server-side, but nothing does for org-flow, so they're checked here against the member's own
// grants (`has` is useAccessStore().has, answered from /api/me/access). A node is kept if any of
// its `access` entries is held; no `access` means always shown. Only checks children, like
// filterAccess.
export function filterByFeatureAccess(feature: Feature, has: (feat: string) => boolean): Feature {
  return {
    ...feature,
    children: feature.children
      ?.map((child) => filterByFeatureAccess(child, has))
      .filter((child) => child.access?.some(has) ?? true)
  }
}

// The org-flow rail: the active nodes the organization has purchased, then -- for everyone but an
// organization administrator, who sees the full tree -- only the nodes the member's own grants
// unlock (filterByFeatureAccess).
export function orgFlowFeatureTree(
  feature: Feature,
  orgFeatureKeys: string[],
  has: (feat: string) => boolean,
  isOrganizationAdministrator: boolean
): Feature {
  const purchased = filterByOrgFeatures(filterInactive(feature), orgFeatureKeys)
  return isOrganizationAdministrator ? purchased : filterByFeatureAccess(purchased, has)
}

export function mutateFeature(
  feature: Feature,
  path: string[],
  realmId: string,
  featureAccess: string[],
  mutation: (feature: Feature) => Feature
): Feature {
  if (path.length === 1 && feature.name === path[0])
    return mutation(feature as unknown as Feature) as unknown as Feature
  return {
    ...feature,
    children: feature.children?.map((child) =>
      mutateFeature(child, path.slice(1), realmId, featureAccess, mutation)
    )
  }
}

export function getFeatureFromPath(
  rootFeature: Feature,
  path: string[],
  featurePath?: string[]
): Feature | undefined {
  let current = rootFeature
  const currentFeaturePath = [...(featurePath ?? path)].slice(1)

  const pathLength = path.length
  path = path.slice(1)

  let depth = 1
  while (path.length > 0) {
    const name = path.shift()
    const featureName = currentFeaturePath.shift()
    if (!featureName && name === '*') return undefined

    for (const child of current.children ?? []) {
      if (
        child.name === name ||
        featureName === 'digital-buildings' ||
        (name === '*' && featureName === child.name)
      ) {
        current = child
        depth++
        break
      }
    }
  }

  if (depth !== pathLength) return undefined

  return current
}

export function setParents(feature: Feature, parent?: Feature) {
  feature.parent = parent
  feature.children?.forEach((child) => setParents(child, feature))
}

export function getFeaturePath(feature: Feature): Feature[] {
  if (!feature.parent) return [feature]
  return [...getFeaturePath(feature.parent), feature]
}

export function reconstructFeaturePath(feature: Feature, path: string[]): string[] {
  let current: Feature | undefined = feature

  path.shift()

  while (path.length > 0 && current) {
    const name = path.shift()

    if (!name) break

    current =
      current.children?.find((child) => child.name === name) ??
      findChildByNameBreadth(current, name)
  }

  return [
    'home',
    ...getFeaturePath(current ?? feature)
      .map((f) => f.name)
      .slice(1)
  ]
}

export function findChildByNameBreadth(feature: Feature, name: string): Feature | undefined {
  if (feature.name === name) return feature
  return searchBreadthFirst(feature, (f) => f.name === name)
}

export function searchBreadthFirst(
  feature: Feature,
  predicate: (feature: Feature) => boolean
): Feature | undefined {
  const queue = [feature]
  while (queue.length > 0) {
    const current = queue.shift()!
    if (predicate(current)) return current
    queue.push(...(current.children ?? []))
  }
}
